import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import React, { useState } from 'react';
import { FormikProvider, useFormik } from 'formik';
import { initCaseFormChange, setFormChanged } from 'app/shared/reducers/case-form-change';
import { Col, Row } from 'reactstrap';
import { Label, TextArea } from 'edozvola-frontend-lib/dist/ui';
import { Button, Input } from 'edozvola-frontend-lib/dist/ui/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PartyTable from './party-table';
import { partiesDataSchema, partyCaseUuidToCopyFromSchema } from './model/party.schema';
import { copyPartiesFromCaseQuery, fetchPartiesDataQuery, savePartiesDataQuery } from 'app/edozvola/cases/parties/service/party.queries';
import PartyForm from './party-form';
import { fetchPartyFileInsightCodebookQuery, fetchPartyNumberCodebookQuery } from 'app/edozvola/codebooks/codebooks.queries';
import FormikValueObserver from 'app/shared/formik-observer/formik-observer';
import { initialPartiesData, PartiesModel } from 'app/edozvola/cases/parties/model/party.model';
import './party.scss';
import { PaginatedDropdown } from 'edozvola-frontend-lib/dist/ui/components/paginated-dropdown';
import { getCaseContext } from 'app/shared/util/context-util';

const Parties = () => {
  const { id: uuid } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const [partyId, setPartyId] = useState<string | null>(null);
  const [readOnly, setReadOnly] = useState(false);
  const { sidebarItemPair } = getCaseContext();

  const { data: parties, isLoading: partiesLoading } = fetchPartiesDataQuery(uuid);
  const { data: partyNumberOptions, isLoading: partyNumberOptionsLoading } = fetchPartyNumberCodebookQuery();
  const { data: partyFileInsightOptions, isLoading: partyFileInsightOptionsLoading } = fetchPartyFileInsightCodebookQuery();

  const copyPartiesFormik = useFormik({
    initialValues: { caseUuidToCopyFrom: '' },
    validationSchema: partyCaseUuidToCopyFromSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: values => copyParties(values.caseUuidToCopyFrom),
  });

  const partiesFormik = useFormik({
    initialValues: parties || initialPartiesData,
    validationSchema: partiesDataSchema,
    onSubmit: (values: PartiesModel) => {
      savePartiesDataMutation(values);
    },
    initialTouched: { partyNumber: true, partyFileInsight: true },
    validateOnMount: true,
    enableReinitialize: true,
  });

  const { mutate: copyParties, isPending: isCopyPartiesPending } = copyPartiesFromCaseQuery(uuid, copyPartiesFormik.resetForm);
  const { mutate: savePartiesDataMutation, isPending: partiesDataSaving } = savePartiesDataQuery(uuid, partiesFormik.resetForm);

  const openNewPartyForm = () => {
    setPartyId('');
    setReadOnly(false);
  };

  return (
    <>
      <FormikProvider value={partiesFormik}>
        <FormikValueObserver
          onChange={dirty => {
            if (dirty.dirty) {
              dispatch(setFormChanged(true));
            } else {
              dispatch(initCaseFormChange());
            }
          }}
        />
        <div className={'form-header'}>Stranke u postupku i opunomoćenici</div>
        <div className={'mt-3 pb-4'}>
          <Row>
            <Col md={5}>
              <Label labelText={'Broj stranaka u postupku'} />
              <PaginatedDropdown
                placeholder={!sidebarItemPair?.readOnly && 'Odaberi broj stranaka u postupku'}
                onItemSelect={selected => partiesFormik.setFieldValue('partyNumber', selected)}
                selected={partiesFormik.values.partyNumber}
                labelKey={'value'}
                options={partyNumberOptions}
                disabled={sidebarItemPair?.readOnly}
                error={!sidebarItemPair?.readOnly && (partiesFormik.errors.partyNumber as string)}
                showErrorMessage
              />
            </Col>
          </Row>
        </div>
      </FormikProvider>
      {!sidebarItemPair?.readOnly && (
        <>
          <div className={'d-flex justify-content-end pb-4 gap-2'}>
            <div className={'case-party-copy'}>
              <Input
                placeholder={'Unesite šifru predmeta'}
                maxLength={255}
                addonAppend={
                  isCopyPartiesPending ? (
                    <FontAwesomeIcon icon="spinner" spin />
                  ) : (
                    <FontAwesomeIcon
                      aria-disabled={
                        (!!copyPartiesFormik.errors && !!copyPartiesFormik.errors.caseUuidToCopyFrom) ||
                        copyPartiesFormik.values.caseUuidToCopyFrom === ''
                      }
                      className={
                        (!!copyPartiesFormik.errors && !!copyPartiesFormik.errors.caseUuidToCopyFrom) ||
                        copyPartiesFormik.values.caseUuidToCopyFrom === ''
                          ? 'disabled'
                          : ''
                      }
                      icon="copy"
                    />
                  )
                }
                onChange={event => {
                  copyPartiesFormik.setFieldValue('caseUuidToCopyFrom', event.target.value);

                  if (copyPartiesFormik.submitCount > 0) {
                    copyPartiesFormik.setErrors({});
                  }
                }}
                addonAppendClick={() => {
                  if (!isCopyPartiesPending) {
                    copyPartiesFormik.handleSubmit();
                  }
                }}
                error={
                  copyPartiesFormik.errors && copyPartiesFormik.errors.caseUuidToCopyFrom ? copyPartiesFormik.errors.caseUuidToCopyFrom : ''
                }
              />
            </div>
            {!sidebarItemPair?.readOnly && (
              <div>
                <Button label={'Dodaj stranku/opunomoćenika'} color={'info'} prependIcon={'plus'} size={'lg'} onClick={openNewPartyForm} />
              </div>
            )}
          </div>
        </>
      )}
      <>
        <PartyTable setParty={setPartyId} setReadOnly={setReadOnly} />
        <hr />
        {partyId !== null && <PartyForm partyId={partyId} setPartyId={setPartyId} readOnly={readOnly} />}
        <>
          <div className={'pb-4'}>
            <Row>
              <Col md={6}>
                <Label labelText={'Uvid u spis'} />
                <PaginatedDropdown
                  placeholder={!sidebarItemPair?.readOnly && 'Odaberi uvid u spis'}
                  onItemSelect={selected => partiesFormik.setFieldValue('partyFileInsight', selected)}
                  selected={partiesFormik.values.partyFileInsight}
                  labelKey={'value'}
                  options={partyFileInsightOptions}
                  disabled={sidebarItemPair?.readOnly}
                  error={!sidebarItemPair?.readOnly && (partiesFormik.errors.partyFileInsight as string)}
                  showErrorMessage
                />
              </Col>
              <Col md={6}>
                <Label labelText={'Obrazloženje'} />
                <TextArea
                  name={'partyFileInsightExplanation'}
                  value={partiesFormik.values?.partyFileInsightExplanation ? partiesFormik.values?.partyFileInsightExplanation : ''}
                  rows={1}
                  disabled={sidebarItemPair?.readOnly}
                  onChange={event => partiesFormik.setFieldValue('partyFileInsightExplanation', event.target.value)}
                />
              </Col>
            </Row>
          </div>
          {!sidebarItemPair?.readOnly && (
            <div className={'case-save-button'}>
              <Button
                label={'Spremi'}
                color={'info'}
                size={'lg'}
                isLoading={partiesDataSaving}
                disabled={!partiesFormik.dirty || !partiesFormik.isValid}
                onClick={() => partiesFormik.handleSubmit()}
              />
            </div>
          )}
        </>
      </>
    </>
  );
};

export default Parties;
